import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getPlantillasDeCliente,
  anadirDelCatalogo,
  editarPlantillaDeCliente,
  enviarAMeta,
  crearPlantillaDeCliente,
  promoverPlantilla,
} from '../../../services/kapsoTemplatesService';
import { useCompanies, nombreDeEmpresa } from '../useCompanies';
import { EstadoBadge, SyncBadge, DriftBadge } from './templateBadges';
import PreviaDePlantilla from '../../../components/template-preview/PreviaDePlantilla';

const MAX_BODY = 1024;

/** Cuántos peldaños se ofrecen. Más de esto no lo ha pedido nadie. */
const PELDANOS = [1, 2, 3, 4, 5];

/**
 * En qué punto del contacto en frío va esta plantilla.
 *
 * Fuera de la escalera («—») no significa desactivada: significa que solo se
 * manda a mano, que es justo lo que quieres para un reenganche.
 */
const Peldano = ({ plantilla, onCambiar }) => (
  <label className='flex items-center gap-2 text-xs text-slate-500'>
    <span>Contacto nº</span>
    <select
      aria-label={`Peldaño de ${plantilla.description || plantilla.key}`}
      value={plantilla.escalation_order ?? ''}
      onChange={e => onCambiar(e.target.value === '' ? null : Number(e.target.value))}
      className='rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none'
    >
      <option value=''>— fuera</option>
      {PELDANOS.map(n => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  </label>
);

/**
 * Editar el texto de la copia de ESTE cliente.
 *
 * Es lo que permite corregir un rechazo de Meta. Guardar no toca el estado de
 * aprobación —el motivo del rechazo hay que poder seguir leyéndolo—, pero deja
 * la aprobación desfasada, y eso habilita el botón de reenviar.
 */
const EditorDeCopia = ({ plantilla, onGuardar, onCerrar, companyId }) => {
  const [texto, setTexto] = useState(plantilla.body_text || '');
  const [guardando, setGuardando] = useState(false);
  const sucio = texto !== plantilla.body_text;

  const guardar = async () => {
    setGuardando(true);
    try {
      await onGuardar({ body_text: texto });
      onCerrar();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className='mt-3 pt-3 border-t border-slate-100'>
      <label
        htmlFor={`copia-${plantilla.key}`}
        className='block text-xs font-medium text-slate-600 mb-1'
      >
        Texto para este cliente
      </label>
      <textarea
        id={`copia-${plantilla.key}`}
        rows={4}
        value={texto}
        onChange={e => setTexto(e.target.value)}
        className='w-full rounded-lg border border-slate-200 p-3 text-sm font-mono focus:border-blue-400 focus:outline-none'
      />
      <div className='mt-1 flex items-center justify-between text-xs'>
        <span className='text-slate-400'>
          Solo cambia la copia de este cliente. El catálogo se queda como está.
        </span>
        <span className={texto.length > MAX_BODY ? 'text-red-600 font-medium' : 'text-slate-400'}>
          {texto.length} / {MAX_BODY}
        </span>
      </div>

      <div className='mt-3'>
        <PreviaDePlantilla cuerpo={texto} companyId={companyId} />
      </div>

      {sucio && plantilla.approval_status === 'approved' && (
        <p className='mt-2 text-xs text-amber-700'>
          Meta aprobó el texto de ahora. Al guardar habrá que <strong>volver a enviarla</strong>{' '}
          para que apruebe el nuevo; hasta entonces no se podrá usar.
        </p>
      )}

      <div className='mt-3 flex items-center gap-3'>
        <button
          type='button'
          onClick={guardar}
          disabled={!sucio || guardando || texto.length === 0 || texto.length > MAX_BODY}
          className='px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400'
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <button type='button' onClick={onCerrar} className='text-sm text-slate-500 hover:underline'>
          Cancelar
        </button>
      </div>
    </div>
  );
};

const NuevaPlantilla = ({ onCrear, companyId }) => {
  const [abierto, setAbierto] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const crear = async () => {
    setEnviando(true);
    try {
      const creada = await onCrear({ description: descripcion, body_text: texto });
      if (creada?.unknown_variables?.length) {
        toast.warn(`Creada, pero no sabemos rellenar: ${creada.unknown_variables.join(', ')}`);
      } else {
        toast.success('Plantilla creada');
      }
      setDescripcion('');
      setTexto('');
      setAbierto(false);
    } catch (err) {
      toast.error(err.message || 'No se ha podido crear');
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <button
        type='button'
        onClick={() => setAbierto(true)}
        className='text-sm font-medium text-blue-700 hover:underline'
      >
        + Escribir una plantilla solo para este cliente
      </button>
    );
  }

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-4 space-y-3'>
      <h4 className='text-sm font-medium text-slate-900'>Nueva plantilla de este cliente</h4>

      <div>
        <label htmlFor='nueva-desc' className='block text-xs font-medium text-slate-600 mb-1'>
          Nombre
        </label>
        <input
          id='nueva-desc'
          type='text'
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder='Aviso de corte de suministro'
          className='w-full rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
        />
      </div>

      <div>
        <label htmlFor='nueva-body' className='block text-xs font-medium text-slate-600 mb-1'>
          Texto del mensaje
        </label>
        <textarea
          id='nueva-body'
          rows={4}
          value={texto}
          onChange={e => setTexto(e.target.value)}
          className='w-full rounded-lg border border-slate-200 p-3 text-sm font-mono focus:border-blue-400 focus:outline-none'
        />
        <div className='mt-1 flex items-center justify-between text-xs'>
          <span className='text-slate-400'>
            Variables entre llaves dobles: <code>{'{{customer_name}}'}</code>
          </span>
          <span className={texto.length > MAX_BODY ? 'text-red-600 font-medium' : 'text-slate-400'}>
            {texto.length} / {MAX_BODY}
          </span>
        </div>
      </div>

      <PreviaDePlantilla cuerpo={texto} companyId={companyId} />

      <div className='flex items-center gap-3'>
        <button
          type='button'
          onClick={crear}
          disabled={enviando || !descripcion.trim() || !texto.trim() || texto.length > MAX_BODY}
          className='px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400'
        >
          {enviando ? 'Creando…' : 'Crear'}
        </button>
        <button
          type='button'
          onClick={() => setAbierto(false)}
          className='text-sm text-slate-500 hover:underline'
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

const KapsoCompanyTemplates = () => {
  const companies = useCompanies();
  const [companyId, setCompanyId] = useState('');
  const [datos, setDatos] = useState(null);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    try {
      setDatos(await getPlantillasDeCliente(companyId));
      setError(null);
    } catch (err) {
      setDatos(null);
      setError(err.message || 'No se han podido cargar las plantillas');
    }
  }, [companyId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const accion = async (fn, exito) => {
    try {
      await fn();
      toast.success(exito);
      await cargar();
    } catch (err) {
      toast.error(err.message || 'No se ha podido completar');
    }
  };

  return (
    <div className='space-y-4'>
      <div className='bg-white rounded-lg border border-slate-200 p-4'>
        <label htmlFor='kapso-company' className='block text-xs font-medium text-slate-600 mb-1'>
          Cliente
        </label>
        <select
          id='kapso-company'
          value={companyId}
          onChange={e => setCompanyId(e.target.value)}
          className='w-full max-w-md rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
        >
          <option value=''>Elige un cliente…</option>
          {companies.map(c => (
            <option key={c._id} value={c._id}>
              {nombreDeEmpresa(companies, c._id)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className='bg-white rounded-lg border border-red-200 p-4 text-sm text-red-700'>
          {error}{' '}
          <button type='button' onClick={cargar} className='underline font-medium'>
            Reintentar
          </button>
        </div>
      )}

      {companyId && datos && (
        <>
          <div className='space-y-3'>
            {/* No se llaman «activas»: esa palabra hacía dos trabajos a la vez
                —«la tiene» y «la puede usar»— y solo una aprobada se puede
                enviar de verdad. El estado lo dice cada fila. */}
            <h3 className='text-sm font-semibold text-slate-900'>
              Plantillas de este cliente ({datos.templates.length})
            </h3>
            {datos.templates.length === 0 && (
              <p className='text-sm text-slate-500'>
                Ninguna. Este cliente <strong>no puede recibir plantillas</strong> hasta que se le
                añada alguna y Meta la apruebe.
              </p>
            )}
            {datos.templates.map(t => (
              <div key={t.key} className='bg-white rounded-lg border border-slate-200 p-4'>
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='text-sm font-medium text-slate-900'>{t.description || t.key}</p>
                    <p className='text-xs text-slate-400 font-mono'>{t.key}</p>
                  </div>
                  <div className='flex items-center gap-2 flex-wrap justify-end'>
                    <span className='px-2 py-0.5 rounded-lg text-[11px] font-medium bg-slate-100 text-slate-600'>
                      {t.source === 'catalogue' ? 'Del catálogo' : 'Propia'}
                    </span>
                    <EstadoBadge estado={t.approval_status} label={t.approval_label} />
                    <SyncBadge sync={t.sync} />
                    <DriftBadge drift={t.catalogue_drift} />
                  </div>
                </div>

                <p className='mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap'>
                  {t.preview_text}
                </p>

                {t.rejection_reason && (
                  <p className='mt-2 text-xs text-red-700'>Meta la rechazó: {t.rejection_reason}</p>
                )}

                <div className='mt-3 flex items-center gap-3 flex-wrap'>
                  <Peldano
                    plantilla={t}
                    onCambiar={orden =>
                      accion(
                        () => editarPlantillaDeCliente(companyId, t.key, { escalation_order: orden }),
                        orden === null
                          ? 'Fuera de la escalera: ahora solo se manda a mano'
                          : `Pasa a ser el contacto nº ${orden}`
                      )
                    }
                  />
                  <span className='text-slate-200'>|</span>
                  <button
                    type='button'
                    disabled={!t.can_submit}
                    onClick={() =>
                      accion(
                        () => enviarAMeta(companyId, t.key),
                        'Enviada. Meta la está revisando: suele tardar minutos, a veces horas.'
                      )
                    }
                    className='px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400'
                  >
                    Enviar a Meta
                  </button>
                  <button
                    type='button'
                    onClick={() => setEditando(editando === t.key ? null : t.key)}
                    className='px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50'
                  >
                    {editando === t.key ? 'Cerrar' : 'Editar texto'}
                  </button>
                  {/* Un botón apagado sin explicación es indistinguible de uno
                      roto, y el motivo más común no se arregla desde aquí. */}
                  {!t.can_submit && t.reason_text && (
                    <span className='text-xs text-slate-500 max-w-lg'>{t.reason_text}</span>
                  )}
                </div>

                {editando === t.key && (
                  <EditorDeCopia
                    plantilla={t}
                    companyId={companyId}
                    onCerrar={() => setEditando(null)}
                    onGuardar={cambios =>
                      accion(
                        () => editarPlantillaDeCliente(companyId, t.key, cambios),
                        "Texto guardado"
                      )
                    }
                  />
                )}

                {t.promotable && (
                  <div className='mt-3 pt-3 border-t border-slate-100 flex items-center gap-3'>
                    <button
                      type='button'
                      onClick={() =>
                        accion(
                          () => promoverPlantilla(companyId, t.key),
                          'Subida al catálogo'
                        )
                      }
                      className='px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50'
                    >
                      Subir al catálogo
                    </button>
                    {/* Meta aprueba por cuenta de negocio: en otro cliente
                        vuelve a empezar. Promover ahorra escribir, no esperar. */}
                    <span className='text-xs text-slate-500'>
                      La ofrece al resto de clientes. Copia el texto, <strong>no la aprobación</strong>.
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className='space-y-3'>
            <h3 className='text-sm font-semibold text-slate-900'>
              Disponibles en el catálogo ({datos.available.length})
            </h3>
            {datos.available.length === 0 ? (
              <p className='text-sm text-slate-500'>Ya tiene todo el catálogo.</p>
            ) : (
              datos.available.map(t => (
                <div
                  key={t.key}
                  className='bg-white rounded-lg border border-slate-200 p-4 flex items-start justify-between gap-4'
                >
                  <div className='min-w-0'>
                    <p className='text-sm font-medium text-slate-900'>{t.description || t.key}</p>
                    <p className='mt-1 text-sm text-slate-500 whitespace-pre-wrap'>{t.preview_text}</p>
                  </div>
                  <button
                    type='button'
                    onClick={() =>
                      accion(
                        () => anadirDelCatalogo(companyId, t.key),
                        'Añadida en borrador. Todavía no se ha enviado a Meta'
                      )
                    }
                    className='flex-shrink-0 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50'
                  >
                    Añadir a este cliente
                  </button>
                </div>
              ))
            )}
          </div>

          <NuevaPlantilla
            companyId={companyId}
            onCrear={async datosNuevos => {
              const creada = await crearPlantillaDeCliente(companyId, datosNuevos);
              await cargar();
              return creada;
            }}
          />
        </>
      )}
    </div>
  );
};

export default KapsoCompanyTemplates;
